package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.ResourceVersion
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The realtime notification envelope.
 *
 * Unlike everything else in this module, this type has no OpenAPI counterpart -- the contract
 * defines the token endpoint (`RealtimeTokenResponse`) but leaves the socket payload to the
 * implementation. It lives here anyway because it crosses the wire between `:server` and the
 * clients, and both compile against this module; declaring it twice is how the two ends drift.
 *
 * It deliberately carries IDS AND NOTHING ELSE. DECISIONS §11: "WebSocket delivery is not
 * treated as durable truth ... after reconnect, the client reconciles messages, edits, and
 * deletion tombstones through REST." A client that rendered a body from this frame would
 * disagree with Postgres the moment one frame was dropped -- and frames ARE dropped, since a
 * publish that fails is not retried. So the only correct reaction to one of these is to go ask
 * REST what changed.
 */
@Serializable
data class RealtimeEvent(
    @SerialName("type") val type: String,
    /** Present for chat events; absent for Discover's global and per-user channels. */
    @SerialName("conversation_id") val conversationId: Uuid? = null,
    /** Absent for events that concern the conversation itself rather than one message. */
    @SerialName("message_id") val messageId: Uuid? = null,
    /** Discover events carry ids only; clients reconcile the actual content through REST. */
    @SerialName("post_id") val postId: Uuid? = null,
    @SerialName("comment_id") val commentId: Uuid? = null,
    @SerialName("group_id") val groupId: Uuid? = null,
    /** Tasks hints identify the REST resource to reconcile, never its progress or health input. */
    @SerialName("task_set_id") val taskSetId: Uuid? = null,
    @SerialName("task_id") val taskId: Uuid? = null,
    /** Version of [taskId], or of [taskSetId] when the event concerns the whole set. */
    @SerialName("version") val version: ResourceVersion? = null,
)

/**
 * The one frame that is NOT a notification about durable state.
 *
 * Typing is stored nowhere and has no REST endpoint to reconcile from, so the doctrine on
 * [RealtimeEvent] -- ids only, go ask REST -- has nothing to point at. Here the frame IS the
 * fact, which is why it carries a payload and why it is a separate type: answering one of these
 * with a REST refetch would perform a message reload per keystroke burst, and an ids-only frame
 * could not tell a client whether somebody had STARTED or STOPPED typing.
 *
 * Being ephemeral, it is published straight to Centrifugo rather than through the transactional
 * outbox. A persisted "is typing" would be republished on the drainer's next pass, announcing a
 * keystroke from minutes ago.
 *
 * The receiving client must expire these on a timer rather than waiting for `is_typing: false`:
 * a stop frame is dropped like any other, and is never sent at all when the sender's app is
 * killed mid-word.
 */
@Serializable
data class TypingEvent(
    @SerialName("type") val type: String,
    @SerialName("conversation_id") val conversationId: Uuid,
    /** WHO is typing. The server publishes to the whole channel, including the sender. */
    @SerialName("user_id") val userId: Uuid,
    @SerialName("is_typing") val isTyping: Boolean,
)

/**
 * Somebody's read watermark moved.
 *
 * WHY THIS IS NOT A [RealtimeEvent]. The ids-only doctrine above says "go ask REST", and for a read
 * receipt that is exactly the behaviour being removed: a tick turning is not worth reloading a
 * conversation for, and until this frame existed a receipt only landed on the next full refresh --
 * which in an open chat meant when the reader's *next message* arrived, or when the screen was left
 * and re-entered. So this frame has to carry the whole fact, like [TypingEvent] does.
 *
 * WHY THE WATERMARK IS A TIMESTAMP AND NOT A SEQ. The durable value is `conversation_members
 * .last_read_seq`, and seq is the right thing on the server. But the FROZEN contract's `Message`
 * carries no sequence number -- `Cache.sq` says so in as many words ("Ordering is by `sent_at`,
 * because the contract's Message carries no sequence number") -- so a client handed a seq has
 * nothing to compare it against, and adding `seq` to `Message` would be a contract change, which is
 * not allowed. The watermark is therefore expressed in the one ordering BOTH ends already share:
 * [readUpToAt] is the `created_at` of the message sitting at the reader's `last_read_seq`.
 *
 * That substitution is sound rather than convenient. `MessageRepository.allocate` takes the
 * `conversation_counters` row lock as the first statement of every send and holds it to COMMIT --
 * the lock-order invariant -- so sends within one conversation are serialised, and `messages
 * .created_at DEFAULT clock_timestamp()` is therefore non-decreasing in `seq`. "Every own message
 * up to seq N" and "every own message at or before that message's created_at" are the same set. The
 * one way they could diverge is a server clock stepping backwards between two sends, which the
 * receiver's monotonic guard (never move the mark backwards) turns into a late tick rather than a
 * wrong one.
 *
 * [userId] is WHO read, not who is being told. The frame goes to the whole conversation channel,
 * including the reader, so a receiver must drop its own -- and in a conversation with more than one
 * other member a single reader's watermark is NOT the conversation's, which is the server's
 * `MIN(last_read_seq)`. A client that cannot establish a sole counterpart must not fold this at all.
 */
@Serializable
data class ReadReceiptEvent(
    @SerialName("type") val type: String,
    @SerialName("conversation_id") val conversationId: Uuid,
    /** WHO read. */
    @SerialName("user_id") val userId: Uuid,
    /** UTC ISO 8601, the `created_at` of the message at the reader's `last_read_seq`. */
    @SerialName("read_up_to_at") val readUpToAt: String,
)

/**
 * Event names, matching the `event_type` column the outbox is written with.
 *
 * Strings rather than an enum: an older client must ignore an event added later, not fail to
 * parse the frame carrying it. Kept out of a companion on [RealtimeEvent] so the serialization
 * plugin owns that companion outright.
 *
 * This object is the SINGLE spelling of each name. Both ends compile against it, so a rename
 * cannot leave the server publishing one string while the clients match on another -- which for
 * an ephemeral frame like [TYPING] would be invisible rather than loud, since nothing persists
 * to disagree with later.
 */
object RealtimeEventTypes {
    const val MESSAGE_NEW = "message.new"
    const val MESSAGE_EDITED = "message.edited"
    const val MESSAGE_DELETED = "message.deleted"
    const val CONVERSATION_DELETED = "conversation.deleted"

    /** Durable, so they travel through the outbox and fit [RealtimeEvent] exactly. */
    const val MESSAGE_PINNED = "message.pinned"
    const val MESSAGE_UNPINNED = "message.unpinned"

    /** Ephemeral. Decoded as [TypingEvent], never as [RealtimeEvent]. */
    const val TYPING = "conversation.typing"

    /**
     * Durable underneath (`last_read_seq`) but published like an ephemeral frame, and decoded as
     * [ReadReceiptEvent]. See `MessageService.publishRead` for why it does not travel through the
     * outbox, and [ReadReceiptEvent] for why it carries state rather than ids.
     */
    const val READ = "conversation.read"

    /** A Discover feed changed on a channel the viewer is already authorized to read. */
    const val DISCOVER_CHANGED = "discover.changed"

    /** The recipient's Activity list and unread badge changed. */
    const val DISCOVER_ACTIVITY = "discover.activity"

    /** Private per-user hints. Full Tasks state is always reconciled through REST. */
    const val TASKS_PROGRESS_UPDATED = "tasks.progress.updated"
    const val TASKS_TASK_COMPLETED = "tasks.task.completed"
    const val TASKS_SET_COMPLETED = "tasks.set.completed"
    const val TASKS_NEW_DAY_AVAILABLE = "tasks.new_day.available"
}
